import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Grid,
  GridItem,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Switch,
  IconButton,
  ButtonGroup,
  Flex,
  Spinner,
  Center,
  Icon,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiTrash2,
  FiEdit3,
  FiChevronLeft,
  FiChevronRight,
  FiSlash,
} from 'react-icons/fi';

import {
  getTeacherAvailability,
  createAvailability,
  updateAvailability,
  deleteAvailability,
  getAvailabilityExceptions,
  createAvailabilityException,
  deleteAvailabilityException,
  getTeacherAppointments,
  generateTimeSlots
} from 'services/appointmentBooking';

// Días de la semana
const DAYS_OF_WEEK = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 0, label: 'Domingo' }
];

// Patrones de repetición
const REPEAT_PATTERNS = [
  { value: 'once', label: 'Solo una vez' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'monthly', label: 'Mensual' }
];

// Pill reutilizable estilo Datofy V1
const Pill = ({ bg, color, children, ...rest }) => (
  <Box
    as="span"
    display="inline-flex"
    alignItems="center"
    gap="4px"
    px="9px"
    py="3px"
    borderRadius="999px"
    bg={bg}
    color={color}
    fontSize="11.5px"
    fontWeight="700"
    {...rest}
  >
    {children}
  </Box>
);

function AvailabilityManager({ teacherId, currentUser }) {
  // Estados principales
  const [availability, setAvailability] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit'
  const [selectedAvailability, setSelectedAvailability] = useState(null);

  // Estado del formulario
  const [formData, setFormData] = useState({
    start_time: '09:00',
    end_time: '17:00',
    duration_minutes: 30,
    day_of_week: 1,
    date: '',
    repeat_pattern: 'weekly',
    repeat_until: '',
    is_recurring: true
  });

  // Estado del modal de bloqueo puntual (excepción)
  const [showExceptionModal, setShowExceptionModal] = useState(false);
  const [exceptionFormData, setExceptionFormData] = useState({
    date: '',
    availability_id: '',
    partial: false,
    start_time: '',
    end_time: '',
    reason: ''
  });

  const toast = useToast();

  // Calcular rango de la semana actual
  const weekRange = useMemo(() => {
    const start = new Date(selectedWeek);
    const dayOfWeek = start.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    start.setDate(start.getDate() + mondayOffset);
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);

    return { start, end };
  }, [selectedWeek]);

  // Obtener días de la semana
  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekRange.start);
      day.setDate(weekRange.start.getDate() + i);
      days.push(day);
    }
    return days;
  }, [weekRange.start]);

  // Cargar datos
  const loadData = async () => {
    setLoading(true);
    try {
      const [availabilityResponse, appointmentsResponse, exceptionsResponse] = await Promise.all([
        getTeacherAvailability(teacherId, {
          dateFrom: weekRange.start.toISOString().split('T')[0],
          dateTo: weekRange.end.toISOString().split('T')[0]
        }),
        getTeacherAppointments(teacherId, {
          dateFrom: weekRange.start.toISOString().split('T')[0],
          dateTo: weekRange.end.toISOString().split('T')[0]
        }),
        getAvailabilityExceptions(teacherId, {
          dateFrom: weekRange.start.toISOString().split('T')[0],
          dateTo: weekRange.end.toISOString().split('T')[0]
        })
      ]);

      if (availabilityResponse.success) {
        setAvailability(availabilityResponse.data || []);
      }

      if (appointmentsResponse.success) {
        setAppointments(appointmentsResponse.data || []);
      }

      if (exceptionsResponse.success) {
        setExceptions(exceptionsResponse.data || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los datos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Efecto para cargar datos
  useEffect(() => {
    if (teacherId) {
      loadData();
    }
  }, [teacherId, weekRange]);

  // Navegación de semanas
  const navigateWeek = (direction) => {
    const newDate = new Date(selectedWeek);
    newDate.setDate(newDate.getDate() + (direction === 'prev' ? -7 : 7));
    setSelectedWeek(newDate);
  };

  // Obtener fecha 'YYYY-MM-DD' del día de la semana dentro de la semana visible
  // day_of_week puede llegar como string "1" desde la API — se fuerza a número
  const getDayOfWeekDate = (dayOfWeek) => {
    const targetDay = weekDays.find(day => day.getDay() === parseInt(dayOfWeek, 10));
    return targetDay ? targetDay.toISOString().split('T')[0] : null;
  };

  // Agrupar disponibilidad por día
  const availabilityByDay = useMemo(() => {
    const grouped = {};

    availability.forEach(item => {
      const date = item.date || getDayOfWeekDate(item.day_of_week);
      const dayKey = new Date(date).toISOString().split('T')[0];

      if (!grouped[dayKey]) {
        grouped[dayKey] = [];
      }

      grouped[dayKey].push(item);
    });

    return grouped;
  }, [availability, weekRange]);

  // Agrupar citas por día
  const appointmentsByDay = useMemo(() => {
    const grouped = {};

    appointments.forEach(appointment => {
      const dayKey = appointment.appointment_date;

      if (!grouped[dayKey]) {
        grouped[dayKey] = [];
      }

      grouped[dayKey].push(appointment);
    });

    return grouped;
  }, [appointments]);

  // Agrupar bloqueos puntuales (excepciones) por día
  const exceptionsByDay = useMemo(() => {
    const grouped = {};

    exceptions.forEach(exception => {
      const dayKey = exception.date;

      if (!grouped[dayKey]) {
        grouped[dayKey] = [];
      }

      grouped[dayKey].push(exception);
    });

    return grouped;
  }, [exceptions]);


  // Handlers del modal
  const openCreateModal = (dayOfWeek = null, date = null) => {
    setFormData({
      start_time: '09:00',
      end_time: '17:00',
      duration_minutes: 30,
      day_of_week: dayOfWeek || 1,
      date: date || '',
      repeat_pattern: 'weekly',
      repeat_until: '',
      // Recurrente por defecto siempre en "Nueva disponibilidad" — antes
      // dependía de si se abría desde el botón general (recurrente) o desde
      // un día puntual del calendario (día específico), lo cual era
      // inconsistente. El admin puede cambiarlo manualmente igual.
      is_recurring: true
    });
    setSelectedAvailability(null);
    setModalMode('create');
    setShowModal(true);
  };

  const openEditModal = (availabilityItem) => {
    setFormData({
      start_time: availabilityItem.start_time,
      end_time: availabilityItem.end_time,
      duration_minutes: availabilityItem.duration_minutes || 30,
      day_of_week: availabilityItem.day_of_week || 1,
      date: availabilityItem.date || '',
      repeat_pattern: availabilityItem.repeat_pattern || 'weekly',
      repeat_until: availabilityItem.repeat_until || '',
      is_recurring: !availabilityItem.date
    });
    setSelectedAvailability(availabilityItem);
    setModalMode('edit');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedAvailability(null);
  };

  // Manejar envío del formulario
  const handleSubmit = async () => {
    try {
      setLoading(true);

      const data = {
        teacher_id: teacherId,
        start_time: formData.start_time,
        end_time: formData.end_time,
        duration_minutes: formData.duration_minutes,
      };

      if (formData.is_recurring) {
        data.repeat_pattern = formData.repeat_pattern; // 'weekly' o 'monthly'
        data.day_of_week    = formData.day_of_week;
        data.repeat_until   = formData.repeat_until;
      } else {
        data.repeat_pattern = 'once';
        data.date           = formData.date;
      }

      let response;
      if (modalMode === 'create') {
        response = await createAvailability(data);
      } else {
        response = await updateAvailability(selectedAvailability.id, data);
      }

      if (response.success) {
        toast({
          title: 'Éxito',
          description: `Disponibilidad ${modalMode === 'create' ? 'creada' : 'actualizada'} correctamente`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        closeModal();
        loadData();
      } else {
        toast({
          title: 'No se pudo guardar',
          description: response.message || 'Error desconocido',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error saving availability:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar la disponibilidad',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Eliminar disponibilidad
  const handleDelete = async (availabilityId) => {
    if (!window.confirm('¿Está seguro de eliminar esta disponibilidad?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await deleteAvailability(availabilityId);

      if (response.success) {
        toast({
          title: 'Éxito',
          description: 'Disponibilidad eliminada correctamente',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        loadData();
      }
    } catch (error) {
      console.error('Error deleting availability:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo eliminar la disponibilidad',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handlers del modal de bloqueo puntual (excepción)
  // Bloquea una fecha específica sin tocar la regla recurrente/específica que la generó.
  // availabilityItem null => bloquea TODOS los bloques de ese día (día completo libre)
  // availabilityItem con valor => bloquea solo ese bloque puntual para esa fecha
  const openBlockDayModal = (dayKey) => {
    setExceptionFormData({
      date: dayKey,
      availability_id: '',
      partial: false,
      start_time: '',
      end_time: '',
      reason: ''
    });
    setShowExceptionModal(true);
  };

  const openBlockBlockModal = (dayKey, availabilityItem) => {
    setExceptionFormData({
      date: dayKey,
      availability_id: availabilityItem.id,
      partial: false,
      start_time: availabilityItem.start_time,
      end_time: availabilityItem.end_time,
      reason: ''
    });
    setShowExceptionModal(true);
  };

  const closeExceptionModal = () => {
    setShowExceptionModal(false);
  };

  const handleCreateException = async () => {
    try {
      setLoading(true);

      const payload = {
        teacher_id: teacherId,
        date: exceptionFormData.date,
        reason: exceptionFormData.reason,
      };

      if (exceptionFormData.availability_id) {
        payload.availability_id = exceptionFormData.availability_id;
      }

      if (exceptionFormData.partial) {
        payload.start_time = exceptionFormData.start_time;
        payload.end_time = exceptionFormData.end_time;
      }

      const response = await createAvailabilityException(payload);

      if (response.success) {
        toast({
          title: 'Éxito',
          description: 'Bloqueo creado correctamente',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        closeExceptionModal();
        loadData();
      } else {
        toast({
          title: 'No se pudo bloquear',
          description: response.message || 'Error desconocido',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error creating availability exception:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo crear el bloqueo',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteException = async (exceptionId) => {
    if (!window.confirm('¿Está seguro de quitar este bloqueo?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await deleteAvailabilityException(exceptionId);

      if (response.success) {
        toast({
          title: 'Éxito',
          description: 'Bloqueo eliminado correctamente',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        loadData();
      }
    } catch (error) {
      console.error('Error deleting availability exception:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo eliminar el bloqueo',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Formatear hora
  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('es-CL', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Renderizar slots de tiempo de un bloque de disponibilidad
  const renderTimeSlots = (availabilityItem, dayAppointments = []) => {
    const slots = generateTimeSlots(
      availabilityItem.start_time,
      availabilityItem.end_time,
      availabilityItem.duration_minutes || 30
    );

    return (
      <HStack spacing={1} flexWrap="wrap">
        {slots.map((slot, index) => {
          // Verificar si el slot está ocupado
          const isBooked = dayAppointments.some(appointment =>
            appointment.start_time === slot.start_time
          );

          return (
            <Pill key={index} bg={isBooked ? 'danger.500' : 'ok.bg'} color={isBooked ? 'white' : 'ok.500'}>
              {formatTime(slot.start_time)}
            </Pill>
          );
        })}
      </HStack>
    );
  };

  return (
    <VStack spacing={6} align="stretch" fontFamily="jakarta">
      {/* Header */}
      <Box bg="white" border="1px solid" borderColor="cream.line" boxShadow="0 4px 6px -1px rgba(43,39,35,0.06)" borderRadius="18px">
        <Box px={5} pt={5} pb={4}>
          <Flex justify="space-between" align="center" direction={{ base: 'column', md: 'row' }} gap={4}>
            <VStack spacing={1} align={{ base: 'center', md: 'start' }}>
              <Text fontSize="xl" fontWeight="800" color="ink.900">
                Gestión de Disponibilidad
              </Text>
              <Text fontSize="sm" color="ink.400">
                Configure sus horarios disponibles para citas con apoderados
              </Text>
            </VStack>

            <Button
              leftIcon={<Icon as={FiPlus} />}
              bg="coral.500"
              color="white"
              fontWeight="700"
              _hover={{ bg: 'coral.600' }}
              _active={{ bg: 'coral.700' }}
              onClick={() => openCreateModal()}
              isDisabled={loading}
            >
              Nueva Disponibilidad
            </Button>
          </Flex>
        </Box>
      </Box>

      {/* Navegación de semana */}
      <Box bg="white" border="1px solid" borderColor="cream.line" boxShadow="0 4px 6px -1px rgba(43,39,35,0.06)" borderRadius="18px">
        <Box p={5}>
          <Flex justify="space-between" align="center">
            <IconButton
              icon={<Icon as={FiChevronLeft} />}
              onClick={() => navigateWeek('prev')}
              isDisabled={loading}
              aria-label="Semana anterior"
              size="sm"
              variant="outline"
              borderColor="cream.line2"
              color="ink.700"
            />

            <VStack spacing={1}>
              <Text fontWeight="800" fontSize="lg" color="ink.900" textTransform="capitalize">
                {weekRange.start.toLocaleDateString('es-CL', {
                  month: 'long',
                  year: 'numeric'
                })}
              </Text>
              <Text fontSize="sm" color="ink.400">
                {weekRange.start.toLocaleDateString('es-CL', { day: 'numeric' })} - {' '}
                {weekRange.end.toLocaleDateString('es-CL', { day: 'numeric' })}
              </Text>
            </VStack>

            <IconButton
              icon={<Icon as={FiChevronRight} />}
              onClick={() => navigateWeek('next')}
              isDisabled={loading}
              aria-label="Semana siguiente"
              size="sm"
              variant="outline"
              borderColor="cream.line2"
              color="ink.700"
            />
          </Flex>
        </Box>
      </Box>

      {/* Loading */}
      {loading && (
        <Center py={8}>
          <VStack spacing={3}>
            <Spinner size="lg" color="coral.500" />
            <Text color="ink.400" fontSize="sm">
              Cargando disponibilidad...
            </Text>
          </VStack>
        </Center>
      )}

      {/* Vista de semana */}
      {!loading && (
        <Grid templateColumns={{ base: '1fr', md: 'repeat(7, 1fr)' }} gap={3}>
          {weekDays.map(day => {
            const dayKey = day.toISOString().split('T')[0];
            const dayAvailability = availabilityByDay[dayKey] || [];
            const dayAppointments = appointmentsByDay[dayKey] || [];
            const dayExceptions = exceptionsByDay[dayKey] || [];
            const isToday = new Date().toDateString() === day.toDateString();
            const isPast = day < new Date().setHours(0, 0, 0, 0);

            // Bloqueo de día completo: sin availability_id (aplica a todos los bloques) y sin horario
            const dayFullBlock = dayExceptions.find(ex => !ex.availability_id && !ex.start_time);

            return (
              <GridItem key={dayKey}>
                <Box
                  border="1px solid"
                  borderColor={isToday ? 'coral.soft' : 'cream.line'}
                  borderRadius="14px"
                  opacity={isPast ? 0.7 : 1}
                  bg="white"
                  boxShadow={isToday ? '0 4px 12px rgba(54,38,22,.1)' : '0 1px 2px rgba(54,38,22,.05)'}
                >
                  <Box px={3} pt={3} pb={2}>
                    <VStack spacing={1}>
                      <Text
                        fontSize="11px"
                        fontWeight="700"
                        color="ink.400"
                        textTransform="capitalize"
                      >
                        {day.toLocaleDateString('es-CL', { weekday: 'short' })}
                      </Text>
                      <Text
                        fontSize="lg"
                        fontWeight="800"
                        color={isToday ? 'coral.600' : 'ink.900'}
                      >
                        {day.getDate()}
                      </Text>
                      {isToday && (
                        <Pill bg="sky.bg" color="sky.ink">Hoy</Pill>
                      )}
                    </VStack>
                  </Box>

                  <Box px={3} pb={3} pt={0}>
                    <VStack spacing={3} align="stretch">
                      {dayFullBlock ? (
                        /* Día completo bloqueado: se oculta la disponibilidad, solo se ofrece desbloquear */
                        <Box p={2} border="1px solid" borderColor="danger.500" borderRadius="9px" bg="danger.bg">
                          <HStack justify="space-between" align="start">
                            <VStack align="start" spacing={0}>
                              <Text fontSize="xs" fontWeight="800" color="danger.500">
                                Día bloqueado
                              </Text>
                              {dayFullBlock.reason && (
                                <Text fontSize="10px" color="danger.500">{dayFullBlock.reason}</Text>
                              )}
                            </VStack>
                            <IconButton
                              icon={<Icon as={FiTrash2} />}
                              size="xs"
                              variant="ghost"
                              color="danger.500"
                              _hover={{ bg: 'white' }}
                              onClick={() => handleDeleteException(dayFullBlock.id)}
                              aria-label="Desbloquear día"
                            />
                          </HStack>
                        </Box>
                      ) : (
                        <>
                          {/* Disponibilidad */}
                          {dayAvailability.length === 0 ? (
                            <Text fontSize="xs" color="ink.400" textAlign="center">
                              Sin disponibilidad
                            </Text>
                          ) : (
                            dayAvailability.map(item => {
                              // Excepciones puntuales que aplican solo a este bloque
                              const itemExceptions = dayExceptions.filter(ex => Number(ex.availability_id) === Number(item.id));
                              const itemFullyBlocked = itemExceptions.some(ex => !ex.start_time);

                              return (
                                <Box key={item.id} p={2} border="1px solid" borderColor="cream.line" borderRadius="9px" bg="cream.bg2" opacity={itemFullyBlocked ? 0.6 : 1}>
                                  <HStack justify="space-between" mb={2}>
                                    <Text fontSize="xs" fontWeight="700" color="ink.900">
                                      {formatTime(item.start_time)} - {formatTime(item.end_time)}
                                    </Text>

                                    <HStack spacing={1}>
                                      <IconButton
                                        icon={<Icon as={FiEdit3} />}
                                        size="xs"
                                        variant="ghost"
                                        color="ink.400"
                                        _hover={{ bg: 'cream.line', color: 'coral.500' }}
                                        onClick={() => openEditModal(item)}
                                        aria-label="Editar"
                                      />
                                      <IconButton
                                        icon={<Icon as={FiSlash} />}
                                        size="xs"
                                        variant="ghost"
                                        color="ink.400"
                                        _hover={{ bg: 'danger.bg', color: 'danger.500' }}
                                        onClick={() => openBlockBlockModal(dayKey, item)}
                                        aria-label="Bloquear este horario"
                                        title="Bloquear solo este horario para esta fecha"
                                        isDisabled={isPast}
                                      />
                                      <IconButton
                                        icon={<Icon as={FiTrash2} />}
                                        size="xs"
                                        variant="ghost"
                                        color="danger.500"
                                        _hover={{ bg: 'danger.bg' }}
                                        onClick={() => handleDelete(item.id)}
                                        aria-label="Eliminar"
                                      />
                                    </HStack>
                                  </HStack>

                                  {itemFullyBlocked ? (
                                    <Text fontSize="10px" fontWeight="700" color="danger.500">Bloqueado esta fecha</Text>
                                  ) : (
                                    <Box>
                                      {renderTimeSlots(item, dayAppointments)}
                                    </Box>
                                  )}

                                  {/* Rangos horarios bloqueados dentro de este bloque */}
                                  {itemExceptions.filter(ex => ex.start_time).map(ex => (
                                    <HStack key={ex.id} justify="space-between" mt={1}>
                                      <Text fontSize="10px" color="danger.500">
                                        Bloqueado {formatTime(ex.start_time)} - {formatTime(ex.end_time)}
                                      </Text>
                                      <IconButton
                                        icon={<Icon as={FiTrash2} />}
                                        size="xs"
                                        variant="ghost"
                                        color="danger.500"
                                        onClick={() => handleDeleteException(ex.id)}
                                        aria-label="Quitar bloqueo"
                                      />
                                    </HStack>
                                  ))}
                                </Box>
                              );
                            })
                          )}

                          {/* Botón agregar para día específico */}
                          <Button
                            size="xs"
                            variant="outline"
                            borderColor="cream.line2"
                            color="ink.700"
                            _hover={{ bg: 'cream.bg2' }}
                            onClick={() => openCreateModal(day.getDay(), dayKey)}
                            leftIcon={<Icon as={FiPlus} />}
                            isDisabled={isPast}
                          >
                            Agregar
                          </Button>

                          {/* Botón bloquear día completo (todos los bloques de esa fecha) */}
                          {dayAvailability.length > 0 && (
                            <Button
                              size="xs"
                              variant="outline"
                              borderColor="danger.500"
                              color="danger.500"
                              _hover={{ bg: 'danger.bg' }}
                              onClick={() => openBlockDayModal(dayKey)}
                              leftIcon={<Icon as={FiSlash} />}
                              isDisabled={isPast}
                            >
                              Bloquear día
                            </Button>
                          )}
                        </>
                      )}
                    </VStack>
                  </Box>
                </Box>
              </GridItem>
            );
          })}
        </Grid>
      )}

      {/* Modal de disponibilidad */}
      <Modal isOpen={showModal} onClose={closeModal} size="xl">
        <ModalOverlay />
        <ModalContent bg="cream.bg" borderRadius="18px" fontFamily="jakarta">
          <ModalHeader color="ink.900" fontWeight="800" borderBottom="1px solid" borderColor="cream.line">
            {modalMode === 'create' ? 'Nueva Disponibilidad' : 'Editar Disponibilidad'}
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={4}>
              {/* Tipo de disponibilidad */}
              <FormControl>
                <FormLabel color="ink.700">Tipo de disponibilidad</FormLabel>
                <HStack spacing={4}>
                  <Switch
                    isChecked={formData.is_recurring}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_recurring: e.target.checked }))}
                    colorScheme="orange"
                  />
                  <Text fontSize="sm" color="ink.900">
                    {formData.is_recurring ? 'Recurrente (semanal/mensual)' : 'Día específico'}
                  </Text>
                </HStack>
              </FormControl>

              {/* Configuración según tipo */}
              {formData.is_recurring ? (
                <HStack spacing={4} w="full">
                  <FormControl>
                    <FormLabel color="ink.700">Día de la semana</FormLabel>
                    <Select
                      value={formData.day_of_week}
                      onChange={(e) => setFormData(prev => ({ ...prev, day_of_week: parseInt(e.target.value) }))}
                      bg="white"
                      focusBorderColor="coral.500"
                    >
                      {DAYS_OF_WEEK.map(day => (
                        <option key={day.value} value={day.value}>
                          {day.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel color="ink.700">Patrón de repetición</FormLabel>
                    <Select
                      value={formData.repeat_pattern}
                      onChange={(e) => setFormData(prev => ({ ...prev, repeat_pattern: e.target.value }))}
                      bg="white"
                      focusBorderColor="coral.500"
                    >
                      {REPEAT_PATTERNS.filter(p => p.value !== 'once').map(pattern => (
                        <option key={pattern.value} value={pattern.value}>
                          {pattern.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </HStack>
              ) : (
                <FormControl>
                  <FormLabel color="ink.700">Fecha específica</FormLabel>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                    bg="white"
                    focusBorderColor="coral.500"
                  />
                </FormControl>
              )}

              <Box w="full" borderTop="1px solid" borderColor="cream.line" />

              {/* Horarios */}
              <HStack spacing={4} w="full">
                <FormControl>
                  <FormLabel color="ink.700">Hora inicio</FormLabel>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                    bg="white"
                    focusBorderColor="coral.500"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel color="ink.700">Hora fin</FormLabel>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                    bg="white"
                    focusBorderColor="coral.500"
                  />
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel color="ink.700">Duración de cada cita (minutos)</FormLabel>
                <NumberInput
                  value={formData.duration_minutes}
                  onChange={(value) => setFormData(prev => ({ ...prev, duration_minutes: parseInt(value) }))}
                  min={15}
                  max={120}
                  step={15}
                >
                  <NumberInputField bg="white" focusBorderColor="coral.500" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              {/* Fecha límite para recurrentes */}
              {formData.is_recurring && (
                <FormControl>
                  <FormLabel color="ink.700">Válido hasta (opcional)</FormLabel>
                  <Input
                    type="date"
                    value={formData.repeat_until}
                    onChange={(e) => setFormData(prev => ({ ...prev, repeat_until: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                    bg="white"
                    focusBorderColor="coral.500"
                  />
                </FormControl>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter borderTop="1px solid" borderColor="cream.line">
            <ButtonGroup spacing={3}>
              <Button variant="outline" borderColor="cream.line2" color="ink.700" onClick={closeModal} isDisabled={loading}>
                Cancelar
              </Button>
              <Button
                bg="coral.500"
                color="white"
                fontWeight="700"
                _hover={{ bg: 'coral.600' }}
                _active={{ bg: 'coral.700' }}
                onClick={handleSubmit}
                isLoading={loading}
                loadingText="Guardando..."
              >
                {modalMode === 'create' ? 'Crear' : 'Actualizar'}
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal de bloqueo puntual (excepción) */}
      <Modal isOpen={showExceptionModal} onClose={closeExceptionModal} size="lg">
        <ModalOverlay />
        <ModalContent bg="cream.bg" borderRadius="18px" fontFamily="jakarta">
          <ModalHeader color="ink.900" fontWeight="800" borderBottom="1px solid" borderColor="cream.line">
            Bloquear fecha
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="ink.400">
                {exceptionFormData.availability_id
                  ? 'Este bloqueo aplica solo al bloque de horario seleccionado, únicamente para esta fecha. La regla de disponibilidad no se modifica ni se elimina.'
                  : 'Este bloqueo aplica a todos los bloques de disponibilidad de este profesor para esta fecha, sin modificar ni eliminar las reglas recurrentes o específicas.'}
              </Text>

              <FormControl>
                <FormLabel color="ink.700">Fecha a bloquear</FormLabel>
                <Input
                  type="date"
                  value={exceptionFormData.date}
                  isReadOnly
                  bg="white"
                  borderColor="cream.line2"
                />
              </FormControl>

              <FormControl>
                <HStack spacing={4}>
                  <Switch
                    isChecked={exceptionFormData.partial}
                    onChange={(e) => setExceptionFormData(prev => ({ ...prev, partial: e.target.checked }))}
                    colorScheme="orange"
                  />
                  <Text fontSize="sm" color="ink.900">
                    {exceptionFormData.partial ? 'Bloquear solo un rango horario' : 'Bloquear el día completo'}
                  </Text>
                </HStack>
              </FormControl>

              {exceptionFormData.partial && (
                <HStack spacing={4} w="full">
                  <FormControl>
                    <FormLabel color="ink.700">Desde</FormLabel>
                    <Input
                      type="time"
                      value={exceptionFormData.start_time}
                      onChange={(e) => setExceptionFormData(prev => ({ ...prev, start_time: e.target.value }))}
                      bg="white"
                      focusBorderColor="coral.500"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel color="ink.700">Hasta</FormLabel>
                    <Input
                      type="time"
                      value={exceptionFormData.end_time}
                      onChange={(e) => setExceptionFormData(prev => ({ ...prev, end_time: e.target.value }))}
                      bg="white"
                      focusBorderColor="coral.500"
                    />
                  </FormControl>
                </HStack>
              )}

              <FormControl>
                <FormLabel color="ink.700">Motivo (opcional)</FormLabel>
                <Input
                  type="text"
                  placeholder="Ej: Feriado, licencia, capacitación..."
                  value={exceptionFormData.reason}
                  onChange={(e) => setExceptionFormData(prev => ({ ...prev, reason: e.target.value }))}
                  bg="white"
                  focusBorderColor="coral.500"
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter borderTop="1px solid" borderColor="cream.line">
            <ButtonGroup spacing={3}>
              <Button variant="outline" borderColor="cream.line2" color="ink.700" onClick={closeExceptionModal} isDisabled={loading}>
                Cancelar
              </Button>
              <Button
                bg="danger.500"
                color="white"
                fontWeight="700"
                _hover={{ opacity: 0.9 }}
                onClick={handleCreateException}
                isLoading={loading}
                loadingText="Bloqueando..."
                isDisabled={exceptionFormData.partial && (!exceptionFormData.start_time || !exceptionFormData.end_time)}
              >
                Bloquear
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}

export default AvailabilityManager;
